import { useState, useEffect } from "react";
import { authClient, Document } from "@/lib/supabase";
import { useToast } from "@/hooks/use-toast";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { RefreshCw } from "lucide-react";

interface RefreshDocumentDialogProps {
  document: Document;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onDocumentRefreshed: () => void;
}

export default function RefreshDocumentDialog({
  document,
  open,
  onOpenChange,
  onDocumentRefreshed,
}: RefreshDocumentDialogProps) {
  const [refreshing, setRefreshing] = useState(false);
  const [isInCooldown, setIsInCooldown] = useState(false);
  const [minutesAgo, setMinutesAgo] = useState(0);
  const { toast } = useToast();

  // Check cooldown status
  useEffect(() => {
    const checkCooldown = () => {
      const lastRefreshStr = localStorage.getItem(`refresh_cooldown_${document.id}`);
      if (lastRefreshStr) {
        const lastRefresh = parseInt(lastRefreshStr, 10);
        const elapsed = Date.now() - lastRefresh;
        const remaining = Math.max(0, 300000 - elapsed); // 5 minutes
        setIsInCooldown(remaining > 0);
        setMinutesAgo(Math.floor(elapsed / 60000));
      } else {
        setIsInCooldown(false);
      }
    };

    if (open) {
      checkCooldown();
    }
  }, [document.id, open]);

  const handleRefresh = async () => {
    setRefreshing(true);

    try {
      // Get authentication token
      const { data: sessionData } = await authClient.auth.getSession();
      const token = sessionData?.session?.access_token;

      if (!token) {
        throw new Error("Not authenticated");
      }

      // Call process-document edge function
      const response = await fetch(
        `${import.meta.env.VITE_DOCS_SUPABASE_URL}/functions/v1/process-document`,
        {
          method: "POST",
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "application/json",
          },
          body: JSON.stringify({ document_id: document.id }),
        }
      );

      // Handle response - may be empty or invalid JSON on error
      const responseText = await response.text();
      let result;
      try {
        result = responseText ? JSON.parse(responseText) : {};
      } catch {
        console.error("Failed to parse response:", responseText);
        throw new Error(
          response.ok ? "Invalid response from server" : `Server error: ${response.status}`
        );
      }

      if (!response.ok) {
        throw new Error(result.error || `Failed to refresh document (${response.status})`);
      }

      // Update last refresh time in localStorage for cooldown
      localStorage.setItem(`refresh_cooldown_${document.id}`, Date.now().toString());

      // Show success toast
      toast({
        title: "Success",
        description: result.size
          ? `Document refreshed successfully (${Math.round(result.size / 1024)} KB)`
          : "Document refresh started",
      });

      // Close dialog and refresh document list
      onOpenChange(false);
      onDocumentRefreshed();
    } catch (error) {
      console.error("Error refreshing document:", error);
      toast({
        title: "Error",
        description: error instanceof Error ? error.message : "Failed to refresh document",
        variant: "destructive",
      });
    } finally {
      setRefreshing(false);
    }
  };

  return (
    <AlertDialog open={open} onOpenChange={onOpenChange}>
      <AlertDialogContent data-testid="refresh-dialog">
        <AlertDialogHeader>
          <AlertDialogTitle>Refresh Documentation?</AlertDialogTitle>
          <AlertDialogDescription id="refresh-dialog-description">
            {isInCooldown && (
              <p className="text-amber-600 mb-2">
                ⚠️ This document was refreshed {minutesAgo} minute{minutesAgo !== 1 ? "s" : ""} ago.
                Are you sure you want to refresh again?
              </p>
            )}
            This will re-download and re-process <strong>{document.title || document.url}</strong>{" "}
            from its source URL. The document will be updated with the latest content.
          </AlertDialogDescription>
        </AlertDialogHeader>
        <AlertDialogFooter>
          <AlertDialogCancel disabled={refreshing} data-testid="refresh-cancel-button">
            Cancel
          </AlertDialogCancel>
          <AlertDialogAction
            onClick={handleRefresh}
            disabled={refreshing}
            data-testid="refresh-confirm-button"
            className="gap-2"
          >
            {refreshing && (
              <RefreshCw className="h-4 w-4 animate-spin" data-testid="refresh-loading-indicator" />
            )}
            {refreshing ? "Refreshing..." : "Refresh"}
          </AlertDialogAction>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
}
